import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Mail, Phone, User } from "lucide-react";
import { useWidgetMode } from "@/lib/widgetMode";

const INTENT_OPTIONS = [
  { value: "sell", label: "Sell" },
  { value: "trade", label: "Trade" },
  { value: "dont_know", label: "Don't Know" },
];

const ContactModal = ({ open, onClose, onSubmit, loading }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [intent, setIntent] = useState("sell");
  const { isCompact } = useWidgetMode();

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name: name.trim(), email: email.trim(), phone: phone.trim(), intent: intent || null });
  };

  const canSubmit = email.trim() || phone.trim();

  return (
    <div
      className={`fixed inset-0 z-50 flex ${
        isCompact ? "items-start" : "items-center"
      } justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto`}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="contact-modal">
        {/* Header */}
        <div className="header-nucar px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">Almost there!</h2>
            <p className="text-slate-300 text-sm mt-0.5">How should we reach you?</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="close-contact-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="text-label mb-1.5 block">YOUR NAME (OPTIONAL)</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="contact-name-input"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="text-label mb-1.5 block">EMAIL ADDRESS</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="contact-email-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">We'll send your offer confirmation here</p>
          </div>

          {/* Phone */}
          <div>
            <label className="text-label mb-1.5 block">PHONE NUMBER</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="tel"
                placeholder="(302) 555-1234"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="contact-phone-input"
              />
            </div>
          </div>

          {/* Intent */}
          <div>
            <label className="text-label mb-1.5 block">WHAT DO YOU WANT TO DO?</label>
            <div className="grid grid-cols-3 gap-2">
              {INTENT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setIntent(option.value)}
                  className={`h-11 rounded-xl border text-sm font-medium transition-colors ${
                    intent === option.value
                      ? "border-slate-800 bg-slate-800 text-white"
                      : "border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                  data-testid={`contact-intent-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={!canSubmit || loading}
              className="w-full h-14 btn-accent disabled:opacity-50 text-base"
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-slate-800/30 border-t-slate-800 rounded-full animate-spin" />
                  Locking Offer...
                </span>
              ) : (
                "Lock My Offer"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
